import React, { useState } from 'react';
import { Box, IconButton, Paper, Popover, Tooltip } from '@mui/material';
import { Container, Draggable } from 'react-smooth-dnd';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import VisibilityIcon from '../../../../assets/icons/visibility-icon';
import useStyles from './styles';
import { VirtualTableHeadCell } from '../../types';
import { useMountEffect } from '../../../../globals/hooks/use-mount-effect';
import theme from '../../../../globals/theme';
import SortDescending from '../../../../assets/icons/sort-descending';
import SortAscending from '../../../../assets/icons/sort-ascending';

type SortProperties = {
  headerColumns: VirtualTableHeadCell[];
  setColumnVisibility: Function;
  columnVisibility: string[];
  setColumnOrder: Function;
  columnOrder: string[];
  storageString: string;
  sortableAccessors?: { [key: string]: boolean };
  reloadTable?: Function;
};
interface Sorting {
  columnName: string;
  direction: 'desc' | 'asc';
}

const ColumnSortVisibility = (sortProperties: SortProperties) => {
  const {
    headerColumns,
    setColumnVisibility,
    columnVisibility,
    setColumnOrder,
    columnOrder,
    storageString,
    sortableAccessors,
    reloadTable,
  } = sortProperties;
  const savedDefaultSortByString = window.localStorage.getItem(`${storageString}-default-sort-by`);
  const savedHidden = window.localStorage.getItem(`${storageString}-hidden-columns`);
  const savedOrder = window.localStorage.getItem(`${storageString}-order-columns`);
  const [anchorElement, setAnchorElement] = React.useState<HTMLButtonElement | null>(null);
  const [sortBy, setSortBy] = useState<Sorting | null>(
    savedDefaultSortByString && JSON.parse(savedDefaultSortByString),
  );

  useMountEffect(() => {
    setColumnOrder(
      savedOrder && savedOrder !== 'null'
        ? JSON.parse(savedOrder)
        : headerColumns.map((headerColumn) => headerColumn.accessor),
    );
    setColumnVisibility(savedHidden && savedHidden !== 'null' ? JSON.parse(savedHidden) : []);
  });

  const handleHideColumn = (accessor: string) => {
    const index = columnVisibility.indexOf(accessor);
    const hiddenItemsToSet = [...columnVisibility];
    if (index >= 0) hiddenItemsToSet.splice(index, 1);
    else hiddenItemsToSet.push(accessor);
    window.localStorage.setItem(
      `${storageString}-hidden-columns`,
      JSON.stringify(hiddenItemsToSet),
    );
    setColumnVisibility(hiddenItemsToSet);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };
  const handleSetDefaultSort = (option: string) => {
    const direction: 'desc' | 'asc' = sortBy?.direction === 'asc' ? 'desc' : 'asc';
    const sorting = { columnName: option, direction };
    window.localStorage.setItem(`${storageString}-default-sort-by`, JSON.stringify(sorting));
    if (reloadTable) reloadTable();
    setSortBy(sorting);
  };

  const open = Boolean(anchorElement);

  const classes = useStyles();

  const onDrop = ({
    removedIndex,
    addedIndex,
  }: {
    removedIndex: number | null;
    addedIndex: number | null;
  }) => {
    if (
      typeof removedIndex === 'number' &&
      removedIndex > -1 &&
      typeof addedIndex === 'number' &&
      addedIndex > -1
    ) {
      const itemsCopy = [...columnOrder];
      itemsCopy.splice(removedIndex, 1);
      itemsCopy.splice(addedIndex, 0, columnOrder[removedIndex]);
      localStorage.setItem(`${storageString}-order-columns`, JSON.stringify(itemsCopy));
      setColumnOrder(itemsCopy);
    }
  };

  return (
    <Tooltip title="Column Order &amp; Visibility" disableHoverListener={open} enterDelay={600}>
      <span>
        <IconButton onClick={handleClick} size="large">
          <VisibilityIcon
            stroke={theme.palette.grey[600]}
            fill={theme.palette.grey[600]}
            width="20px"
            height="20px"
          />
        </IconButton>
        {open && (
          <Popover
            open={open}
            anchorEl={anchorElement}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Paper className={classes.popoverContainer}>
              <Container dragHandleSelector=".drag-handle" lockAxis="y" onDrop={onDrop}>
                {columnOrder.map((option: any) => {
                  const title = headerColumns.find(
                    (headerColumn) => headerColumn.accessor === option,
                  );
                  const visible = columnVisibility.includes(option)
                    ? theme.palette.grey[400]
                    : theme.palette.grey[900];
                  const activeSort = sortBy?.columnName === option;
                  return (
                    <Draggable key={option}>
                      <Box className={classes.option}>
                        <Tooltip title="Toggle Visibility" enterDelay={600}>
                          <span>
                            <IconButton
                              className={classes.visibilityButton}
                              onClick={() => handleHideColumn(option)}
                              size="large">
                              <VisibilityIcon width="13px" height="13px" stroke={visible} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <div className={classes.optionText} style={{ color: visible }}>
                          {title?.Header || 'undefined'}
                        </div>
                        {sortableAccessors && sortableAccessors[option] && (
                          <Tooltip title="Default Sort" enterDelay={600}>
                            <span>
                              <IconButton
                                className={classes.sortButton}
                                onClick={() => handleSetDefaultSort(option)}
                                size="large">
                                {activeSort && sortBy?.direction === 'asc' ? (
                                  <SortAscending
                                    height="13"
                                    fill={!activeSort ? theme.palette.grey[400] : visible}
                                  />
                                ) : (
                                  <SortDescending
                                    height="13"
                                    fill={!activeSort ? theme.palette.grey[400] : visible}
                                  />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Reorder Columns" enterDelay={600}>
                          <div className="drag-handle">
                            <IconButton
                              className={classes.visibilityButton}
                              onClick={() => handleHideColumn(option)}
                              size="large">
                              <DragHandleIcon className={classes.dragHandleIcon} />
                            </IconButton>
                          </div>
                        </Tooltip>
                      </Box>
                    </Draggable>
                  );
                })}
              </Container>
            </Paper>
          </Popover>
        )}
      </span>
    </Tooltip>
  );
};

export default ColumnSortVisibility;
